/**
 * Prüfung einer Vorlage vor dem Aktivieren (VB-19), dieselben Regeln wie app.contract_template_findings in der Datenbank
 * (Migration 0112, Gleichlauf-Test tests/db/99zzzzzz_vertragsbaukasten.test.ts). Die Datenbank rendert nicht; geprüft wird
 * der Vorlagentext selbst: Positivliste, Platzhalter gegen den Katalog der Systemwerte und die eigenen Angaben, Aufbau der
 * Bedingungen, Schema der eigenen Angaben und der Bausteine, Unterschriftsfeld je unterschreibender Partei, kein Text nach
 * der letzten Unterschrift, kein Unterschriftsfeld im vertraulichen Teil, Größen.
 *
 * Dazu Hinweise nur für den Baukasten (nicht im Server, sperren nie): optionales Feld mitten im Satz (VB-21) und ein
 * vertraulicher Teil, der die Partei nennt, die ihn nicht sieht (VB-20).
 */
import {
  codePoints, fieldPath, trimAscii, validateBlocks, walkBlocks,
  type Block, type Inline,
} from "./contract-blocks.js";
import { compileBlocks } from "./contract-blocks-compile.js";
import { CONTRACT_LIMITS, contractMarkupProblem, contractRefProblem } from "./contract-markup.js";
import { CONTRACT_FILTERS } from "./contract-render.js";
import { contractValueTypeOk } from "./contract-source-match.js";
import { CONTRACT_ROLES, isContractRole, isSystemVariablePath, type ContractRole, type CustomVariableDef } from "./contract-variables.js";
import type { Finding, Language } from "./types.js";

export type TemplateCheckCode =
  | "EMPTY_BODY"
  | "MARKUP_NOT_ALLOWED"
  | "TEMPLATE_TOO_LARGE"
  | "CONDITION_STRUCTURE"
  | "UNKNOWN_PLACEHOLDER"
  | "INVALID_VARIABLES"
  | "INVALID_VARIABLE_KEY"
  | "FILLABLE_ROLE_NOT_SIGNER"
  | "NO_SIGNERS"
  | "MISSING_SIGNATURE_BLOCK"
  | "UNREQUESTED_SIGNATURE_BLOCK"
  | "SIGNATURE_IN_PRIVATE_PART"
  /** Unterschriftsfeld in einem „Nur wenn“ (Prüfung 0–2, Befund 10) */
  | "SIGNATURE_IN_CONDITION"
  | "TEXT_AFTER_SIGNATURE"
  | "PRIVATE_PART_AGENCY"
  | "INVALID_BLOCKS"
  /** Bausteine ergeben einen anderen Text als body_html/annex_html (Prüfung 0–2, Befund 13) */
  | "BLOCKS_MISMATCH"
  | "SAMPLE_NOTICE";

/** Hinweise nur im Baukasten */
export type BlockHintCode = "OPTIONAL_FIELD_IN_SENTENCE" | "PRIVATE_PART_NAMES_HIDDEN_PARTY";

export interface TemplateSourceInput {
  bodyHtml: string;
  annexHtml: string | null;
  /** eigene Angaben, wie gespeichert (unbekannte Form wird gemeldet) */
  variables: unknown;
  requiredSigners: readonly ContractRole[];
  annexHiddenFrom: readonly ContractRole[];
  /** Bausteine der Vorlage, falls vorhanden (Schema-Prüfung) */
  blocks?: unknown;
}

const TYPES = ["text", "money", "date", "percent", "number", "boolean", "lines"];
const KEY_RX = /^[a-z][a-z0-9_]{0,63}$/;
const WS_CLASS = "[ \\t\\r\\n\\f\\v]";
const TOKEN_RX = () => /\{\{([^{}]*)\}\}/g;
const IF_RX = new RegExp(`^#if${WS_CLASS}+[^ \\t\\r\\n\\f\\v]`);
const CONTROL_RX = new RegExp(`\\{\\{${WS_CLASS}*(#if${WS_CLASS}[^{}]*|else|/if|signature:[^{}]*)${WS_CLASS}*\\}\\}`, "g");
/** Unterschriftsfeld als Markup (jede Attributreihenfolge der Positivliste) und Platzhalter in Textreihenfolge (gleich 0114) */
const DIV_RX = () => /<div((?:[ \t\r\n\f]+[a-z][a-z0-9-]*="[^"<>]*")*)[ \t\r\n\f]*>/g;
const SIG_OR_TOKEN_RX = () => /\{\{([^{}]*)\}\}|<div((?:[ \t\r\n\f]+[a-z][a-z0-9-]*="[^"<>]*")*)[ \t\r\n\f]*>/g;
const SIG_CLASS_RX = /[ \t\r\n\f]class="tk-signature"/;

type Token = { kind: "if" | "else" | "endif" | "sig" | "var"; path: string; filters: string[] };

function classify(raw: string): Token {
  const inner = trimAscii(raw);
  if (inner === "else") return { kind: "else", path: "", filters: [] };
  if (inner === "/if") return { kind: "endif", path: "", filters: [] };
  if (IF_RX.test(inner)) return { kind: "if", path: trimAscii(inner.slice(3)), filters: [] };
  if (inner.startsWith("signature:")) return { kind: "sig", path: trimAscii(inner.slice(10)), filters: [] };
  const parts = inner.split("|").map(trimAscii);
  return { kind: "var", path: parts[0] ?? "", filters: parts.slice(1) };
}

function tokensOf(html: string): Token[] {
  return [...html.matchAll(TOKEN_RX())].map((m) => classify(m[1] ?? ""));
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const absent = (v: unknown) => v === undefined || v === null;
/** leerer Standardwert (wie isEmpty im Renderer): gilt als nicht gesetzt */
const isBlankDefault = (v: unknown) => absent(v) || (typeof v === "string" && v.trim() === "");

/**
 * Erster Verstoß gegen das Schema der eigenen Angaben oder null (gleich app.contract_variables_problem). `key:<schlüssel>`
 * meldet einen ungültigen Schlüssel, sonst Kennungen wie `type:<schlüssel>`, `duplicate:<schlüssel>`, `not_array`.
 */
export function contractVariablesProblem(v: unknown): string | null {
  if (!Array.isArray(v)) return "not_array";
  if (v.length > CONTRACT_LIMITS.variables) return "too_many";
  const seen = new Set<string>();
  for (const e of v) {
    if (!isObj(e) || typeof e.key !== "string") return "entry";
    const key = e.key;
    if (!KEY_RX.test(key)) return `key:${key}`;
    if (seen.has(key)) return `duplicate:${key}`;
    seen.add(key);
    const label = e.label;
    if (!isObj(label) || typeof label.de !== "string" || trimAscii(label.de) === "" || codePoints(label.de) > 120
      || !(absent(label.en) || (typeof label.en === "string" && codePoints(label.en) <= 120))) return `label:${key}`;
    if (typeof e.type !== "string" || !TYPES.includes(e.type)) return `type:${key}`;
    if (!(e.required === undefined || typeof e.required === "boolean")) return `required:${key}`;
    if (!(absent(e.fillable_by) || isContractRole(e.fillable_by))) return `fillable_by:${key}`;
    if (!(absent(e.prefill) || (typeof e.prefill === "string" && isSystemVariablePath(e.prefill)))) return `prefill:${key}`;
    if (!(absent(e.options) || (Array.isArray(e.options) && e.options.length <= 100 && e.options.every((o) => typeof o === "string" && codePoints(o) <= 200)))) return `options:${key}`;
    if (!(absent(e.default) || ["string", "number", "boolean"].includes(typeof e.default))) return `default:${key}`;
    // Runde 3, R3-05: der Standardwert passt zum Typ, wie ihn der Renderer des Servers verlangt (sonst gilt jeder Vertrag aus der Vorlage als abweichend)
    if (!isBlankDefault(e.default) && !contractValueTypeOk(e.default, e.type)) return `default:${key}`;
    if (!(absent(e.ui) || e.ui === "choice")) return `ui:${key}`;
    const h = e.help;
    if (!(absent(h) || (isObj(h) && (absent(h.de) || (typeof h.de === "string" && codePoints(h.de) <= 1000))
      && (absent(h.en) || (typeof h.en === "string" && codePoints(h.en) <= 1000))))) return `help:${key}`;
  }
  return null;
}

/**
 * Statische Prüfung einer Vorlage (ohne Rendern), in derselben Reihenfolge wie die Datenbank. `error` sperrt das Aktivieren,
 * `hint` nicht.
 */
export function checkTemplateSource(input: TemplateSourceInput): Finding[] {
  const findings: Finding[] = [];
  const add = (code: TemplateCheckCode, severity: Finding["severity"], params?: Record<string, string | number>) => {
    findings.push(params ? { code, severity, params } : { code, severity });
  };
  const body = input.bodyHtml ?? "";
  const annex = input.annexHtml;
  const parts: { part: "body" | "annex"; source: string }[] = [{ part: "body", source: body }];
  if (annex !== null && annex !== undefined) parts.push({ part: "annex", source: annex });

  // 1 Text vorhanden
  const bodyTokens = tokensOf(body);
  if (bodyTokens.length === 0 && body.replace(/<[^>]*>/g, "").replace(/&nbsp;|[ \t\r\n\f\v ]/g, "") === "") add("EMPTY_BODY", "error");
  // 2 Positivliste (Prüfung 0–2: auch Zeichenreferenzen, Befunde 15 und 16), 3 Größe
  for (const { part, source } of parts) {
    const problem = contractMarkupProblem(source) ?? contractRefProblem(source);
    if (problem) add("MARKUP_NOT_ALLOWED", "error", { part, problem });
  }
  for (const { part, source } of parts) {
    if (codePoints(source) > CONTRACT_LIMITS.htmlChars) add("TEMPLATE_TOO_LARGE", "error", { part, limit: CONTRACT_LIMITS.htmlChars });
  }
  // 4 Bedingungen, 5 Platzhalter
  const variablesProblem = contractVariablesProblem(input.variables);
  const keys = new Set<string>();
  const fillable = new Map<string, ContractRole>();
  if (Array.isArray(input.variables)) {
    for (const e of input.variables) {
      if (!isObj(e) || typeof e.key !== "string") continue;
      if (keys.has(e.key)) continue;
      keys.add(e.key);
      if (isContractRole(e.fillable_by)) fillable.set(e.key, e.fillable_by);
    }
  }
  const unknown = new Set<string>();
  const referenced = new Set<string>();
  const sigRoles: Record<"body" | "annex", Set<string>> = { body: new Set(), annex: new Set() };
  let annexSignature = false;
  for (const { part, source } of parts) {
    const toks = part === "body" ? bodyTokens : tokensOf(source);
    const stack: boolean[] = [];
    let broken = false;
    for (const tk of toks) {
      if (tk.kind === "if") stack.push(false);
      else if (tk.kind === "else") {
        if (!stack.length || stack[stack.length - 1]) broken = true;
        else stack[stack.length - 1] = true;
      } else if (tk.kind === "endif") {
        if (!stack.length) broken = true;
        else stack.pop();
      }
    }
    if (broken || stack.length) add("CONDITION_STRUCTURE", "error", { part });
    // Unterschriftsfelder als Markup zählen wie Platzhalter (Prüfung 0–2, Befund 5)
    for (const m of source.matchAll(DIV_RX())) {
      if (!SIG_CLASS_RX.test(m[1] ?? "")) continue;
      if (part === "annex") annexSignature = true;
      const role = /data-role="([a-z]+)"/.exec(m[1] ?? "")?.[1];
      if (isContractRole(role)) sigRoles[part].add(role);
    }
    for (const tk of toks) {
      if (tk.kind === "sig") {
        if (part === "annex") annexSignature = true;
        if (isContractRole(tk.path)) { sigRoles[part].add(tk.path); continue; }
        const id = `signature:${tk.path}`;
        if (!unknown.has(id)) { unknown.add(id); add("UNKNOWN_PLACEHOLDER", "error", { path: id, part }); }
        continue;
      }
      if (tk.kind !== "var" && tk.kind !== "if") continue;
      const isVar = tk.path.startsWith("var.");
      const known = isVar ? keys.has(tk.path.slice(4)) : isSystemVariablePath(tk.path);
      if (!known) {
        if (!unknown.has(tk.path)) { unknown.add(tk.path); add("UNKNOWN_PLACEHOLDER", "error", { path: tk.path, part }); }
        continue;
      }
      if (isVar) referenced.add(tk.path.slice(4));
      for (const f of tk.filters) {
        if ((CONTRACT_FILTERS as readonly string[]).includes(f)) continue;
        const id = `${tk.path}|${f}`;
        if (!unknown.has(id)) { unknown.add(id); add("UNKNOWN_PLACEHOLDER", "error", { path: tk.path, filter: f, part }); }
      }
    }
  }
  // 6/7 eigene Angaben
  if (variablesProblem?.startsWith("key:")) add("INVALID_VARIABLE_KEY", "error", { variable: variablesProblem.slice(4) });
  else if (variablesProblem) add("INVALID_VARIABLES", "error", { problem: variablesProblem });
  // 8 Ausfüllfeld einer Partei, die nicht unterschreibt
  for (const [key, role] of fillable) {
    if (referenced.has(key) && !input.requiredSigners.includes(role)) add("FILLABLE_ROLE_NOT_SIGNER", "error", { variable: key, fillable_by: role });
  }
  // 9–12 Unterschriften
  if (input.requiredSigners.length === 0) add("NO_SIGNERS", "error");
  for (const r of CONTRACT_ROLES) if (input.requiredSigners.includes(r) && !sigRoles.body.has(r)) add("MISSING_SIGNATURE_BLOCK", "error", { role: r });
  for (const r of CONTRACT_ROLES) {
    if ((sigRoles.body.has(r) || sigRoles.annex.has(r)) && !input.requiredSigners.includes(r)) add("UNREQUESTED_SIGNATURE_BLOCK", "error", { role: r });
  }
  if (annexSignature) add("SIGNATURE_IN_PRIVATE_PART", "error");
  // Unterschriftsfelder in „Nur wenn“ (Befund 10); 13 Text nach dem ersten Unterschriftsfeld außerhalb jeder Bedingung: danach
  // stehen nur noch Unterschriftsfelder (Text zwischen den Feldern ist so wenig gedeckt wie Text danach)
  // Runde 2, R2-P2: zwischen zwei Feldern ist eine kurze Beschriftung aus festem Text erlaubt (höchstens
  // CONTRACT_LIMITS.signatureLabel sichtbare Zeichen, kein Platzhalter, keine Bedingung); nach dem letzten Feld nichts
  let depth = 0;
  const sigs: { start: number; end: number }[] = [];
  let conditional = false;
  for (const m of body.matchAll(SIG_OR_TOKEN_RX())) {
    const end = m.index + m[0].length;
    if (m[1] !== undefined) {
      const tk = classify(m[1]);
      if (tk.kind === "if") depth += 1;
      else if (tk.kind === "endif") depth = Math.max(depth - 1, 0);
      else if (tk.kind === "sig") { if (depth > 0) conditional = true; else sigs.push({ start: m.index, end }); }
    } else if (SIG_CLASS_RX.test(m[2] ?? "")) {
      if (depth > 0) conditional = true; else sigs.push({ start: m.index, end });
    }
  }
  if (conditional) add("SIGNATURE_IN_CONDITION", "error");
  const labelRest = (x: string) => x.replace(CONTROL_RX, "").replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#0*160(?![0-9]);?|&#[xX]0*[aA]0(?![0-9a-fA-F]);?|[ \t\r\n\f\v]/g, "");
  if (sigs.length) {
    const tailText = labelRest(body.slice(sigs[sigs.length - 1]!.end)) !== "";
    const gapText = sigs.slice(1).some((sg, i) => {
      const gap = body.slice(sigs[i]!.end, sg.start);
      return /\{\{/.test(gap) || Array.from(labelRest(gap)).length > CONTRACT_LIMITS.signatureLabel;
    });
    if (tailText || gapText) add("TEXT_AFTER_SIGNATURE", "error");
  }
  // 14 vertraulicher Teil: die Agentur sieht ihn immer
  if (input.annexHiddenFrom.includes("agency")) add("PRIVATE_PART_AGENCY", "error");
  // 15 Bausteine
  if (input.blocks !== undefined && input.blocks !== null) {
    const problem = validateBlocks(input.blocks);
    if (problem) add("INVALID_BLOCKS", "error", { problem });
    else if (!blocksMatchSource(input.blocks as Block[], body, annex ?? null, input.annexHiddenFrom)) add("BLOCKS_MISMATCH", "error");
  }
  // 16 Hinweis im Vorlagenkopf
  if (body.includes('<p class="tk-sample-notice">') || (annex ?? "").includes('<p class="tk-sample-notice">')) add("SAMPLE_NOTICE", "hint");
  return findings;
}

/**
 * Ergeben die Bausteine genau diesen Vorlagentext (Hauptteil, vertraulicher Teil, verborgen vor)? Die Sprache steht nur in
 * „Anlage n“ bzw. „Annex n“; beide gelten. Gleich app.contract_blocks_match (0114, Befund 13).
 */
export function blocksMatchSource(blocks: readonly Block[], bodyHtml: string, annexHtml: string | null, annexHiddenFrom: readonly ContractRole[]): boolean {
  const hidden = [...annexHiddenFrom].sort().join(",");
  return (["de", "en"] as const).some((language) => {
    const c = compileBlocks(blocks, { language });
    return c.bodyHtml === bodyHtml && c.annexHtml === annexHtml && (annexHtml === null || [...c.annexHiddenFrom].sort().join(",") === hidden);
  });
}

/** Sperrt einer der Befunde das Aktivieren? */
export function templateActivationBlocked(findings: readonly Finding[]): boolean {
  return findings.some((f) => f.severity === "error");
}

// ---------------------------------------------------------------------------
// Baukasten: Prüfung am Baustein-Dokument
// ---------------------------------------------------------------------------

export interface CheckTemplateBlocksOptions {
  language: Language;
  variables?: readonly CustomVariableDef[];
}

/** Optionales Feld (nicht Pflicht, ohne Standard, niemand füllt es beim Unterschreiben) ohne eigenes „Nur wenn“ im Satz. */
function optionalFieldHints(blocks: readonly Block[], defs: readonly CustomVariableDef[]): Finding[] {
  const optional = new Set(defs.filter((d) => !d.required && (d.default === undefined || d.default === null || d.default === "") && !d.fillable_by).map((d) => d.key));
  const out: Finding[] = [];
  const seen = new Set<string>();
  const visit = (nodes: readonly Inline[], guarded: ReadonlySet<string>, blockId: string) => {
    for (const n of nodes) {
      if (n.t === "field" && n.field.src === "custom" && optional.has(n.field.path) && !guarded.has(n.field.path) && !seen.has(n.field.path)) {
        seen.add(n.field.path);
        out.push({ code: "OPTIONAL_FIELD_IN_SENTENCE", severity: "warning", params: { variable: n.field.path, block: blockId } });
      }
      if (n.t === "if") {
        const g = new Set(guarded);
        if (n.when.src === "custom") g.add(n.when.path);
        visit(n.then, g, blockId);
        if (n.else) visit(n.else, guarded, blockId);
      }
    }
  };
  for (const { block, parents } of walkBlocks(blocks)) {
    const guarded = new Set(parents.flatMap((p) => (p.type === "condition" && p.when.src === "custom" ? [p.when.path] : p.type === "choice" ? [p.key] : [])));
    for (const text of inlineOf(block)) visit(text, guarded, block.id);
  }
  return out;
}

function inlineOf(b: Block): Inline[][] {
  switch (b.type) {
    case "heading": case "paragraph": case "note": return [b.text];
    case "list": return b.items.map((it) => it.text);
    case "table": return [...(b.header ?? []), ...b.rows.flat()];
    default: return [];
  }
}

const HIDDEN_PARTY_WORDS: Record<ContractRole, RegExp> = {
  talent: /\b(der|des|dem|den) Creators?\b|\bthe Creator\b|\bCreator (shall|must|agrees|undertakes)\b/i,
  brand: /\b(der|die) Marke\b|\bthe Brand\b/i,
  agency: /$^/,
};

/** Vertraulicher Teil nennt eine Partei, die ihn nicht sieht (Rollen-Chips oder Wortliste). */
function privatePartHints(blocks: readonly Block[]): Finding[] {
  const priv = blocks.find((b) => b.type === "private");
  if (!priv || priv.type !== "private") return [];
  const hidden = CONTRACT_ROLES.filter((r) => !priv.visibleTo.includes(r));
  const out: Finding[] = [];
  for (const role of hidden) {
    let hit = false;
    for (const { block } of walkBlocks(priv.children)) {
      for (const text of inlineOf(block)) {
        const flat = JSON.stringify(text);
        if (HIDDEN_PARTY_WORDS[role].test(text.map((n) => (n.t === "text" ? n.v : " ")).join(""))) hit = true;
        if (flat.includes(`"path":"${role}.`)) hit = true;
      }
    }
    if (hit) out.push({ code: "PRIVATE_PART_NAMES_HIDDEN_PARTY", severity: "warning", params: { role } });
  }
  return out;
}

/**
 * Prüfung einer Vorlage aus Bausteinen: Schema, dann die Regeln des Servers am übersetzten Ergebnis, dazu die Hinweise des
 * Baukastens. Den gerenderten Text (Lückentext, fehlende Werte) prüft danach checkContract wie bisher.
 */
export function checkTemplateBlocks(blocks: unknown, opts: CheckTemplateBlocksOptions): Finding[] {
  const problem = validateBlocks(blocks);
  if (problem) return [{ code: "INVALID_BLOCKS", severity: "error", params: { problem } }];
  const list = blocks as Block[];
  const compiled = compileBlocks(list, { language: opts.language, variables: opts.variables });
  return [
    ...checkTemplateSource({
      bodyHtml: compiled.bodyHtml, annexHtml: compiled.annexHtml, variables: compiled.variables, requiredSigners: compiled.requiredSigners,
      annexHiddenFrom: compiled.annexHiddenFrom, blocks: list,
    }),
    ...optionalFieldHints(list, compiled.variables),
    ...privatePartHints(list),
  ];
}

/** Platzhalterpfade eines Baustein-Dokuments (Felder und Bedingungen), für Editor und Katalog. */
export function blockFieldPaths(blocks: readonly Block[]): string[] {
  const out = new Set<string>();
  const visit = (nodes: readonly Inline[]) => {
    for (const n of nodes) {
      if (n.t === "field") out.add(fieldPath(n.field));
      if (n.t === "if") { out.add(fieldPath(n.when)); visit(n.then); if (n.else) visit(n.else); }
    }
  };
  for (const { block } of walkBlocks(blocks)) {
    for (const text of inlineOf(block)) visit(text);
    if (block.type === "condition") out.add(fieldPath(block.when));
    if (block.type === "field") out.add(fieldPath(block.field));
    if (block.type === "list") for (const it of block.items) if (it.when) out.add(fieldPath(it.when));
  }
  return [...out];
}

// ---------------------------------------------------------------------------
// Abweichung eines Vertrags von seiner Vorlage (VB-22; gleich app.contract_text_norm und contracts.text_differs_from_template)
// ---------------------------------------------------------------------------

/** Text ohne Leerraum zwischen Auszeichnungen und Platzhaltern; der Abschnitts-Editor stellt beim Speichern nur Leerraum um. */
export function contractTextNorm(html: string | null | undefined): string {
  return (html ?? "").replace(/[ \t\r\n\f\v]+/g, " ").replace(/(>|\}\}) (<|\{\{)/g, "$1$2").replace(/^ | $/g, "");
}

/** Weicht der Vertragstext (Hauptteil, vertraulicher Teil) von der Vorlagenfassung ab? */
export function contractTextDiffers(contract: { body_html: string | null; annex_html: string | null },
  template: { body_html: string | null; annex_html: string | null } | null): boolean {
  if (!template) return true;
  return contractTextNorm(contract.body_html) !== contractTextNorm(template.body_html) || contractTextNorm(contract.annex_html) !== contractTextNorm(template.annex_html);
}
